import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { TrendUp, Wallet, ChartLineUp, Percent, Eye, EyeSlash, ShareNetwork, Copy, Trash, Link as LinkIcon, Plus, ArrowUp, ArrowDown, Receipt, CurrencyDollar, Lock } from "phosphor-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GeniLoader from "../components/GeniLoader";
import { useLanguage } from "../context/LanguageContext";
import { useSharedProfile } from "../context/SharedProfileContext";

// Footer branding
const LOGO_PUREBRAIN = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/gtg4vc6a_purebrain.png";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ShareModal from "../components/ShareModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MetricCard = ({ title, value, subtitle, icon: Icon, highlight = false, hidden = false }) => (
  <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
    <div className="flex items-start justify-between mb-3">
      <div className="text-white/40 text-xs font-mono uppercase tracking-wider">{title}</div>
      {Icon && <Icon size={20} weight="duotone" className="text-white/40" />}
    </div>
    <div className={`text-3xl font-bold font-mono mb-1 ${highlight ? highlight : 'text-white'}`}>
      {hidden ? "*****" : value}
    </div>
    {subtitle && <div className="text-white/60 text-sm">{hidden ? "" : subtitle}</div>}
  </div>
);

export default function Performance() {
  const { t, language } = useLanguage();
  
  // Shared/Public profile mode - when viewing another user's profile
  const { isSharedProfileView, isReadOnly } = useSharedProfile();
  
  const [loading, setLoading] = useState(true);
  const [initialCapital, setInitialCapital] = useState(10000);
  const [editingCapital, setEditingCapital] = useState(false);
  const [tempCapital, setTempCapital] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [dateFilter, setDateFilter] = useState("30"); // days
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // In shared/public profile mode, always enable privacy mode for capital/USD values
  const [privacyMode, setPrivacyMode] = useState(() => {
    return localStorage.getItem("performancePrivacyMode") === "true";
  });
  const [shares, setShares] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Balance Adjustments state
  const [adjustments, setAdjustments] = useState([]);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [adjustmentType, setAdjustmentType] = useState("deposit");
  const [adjustmentNote, setAdjustmentNote] = useState("");
  const [adjustmentDate, setAdjustmentDate] = useState("");
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [showAdjustmentsPanel, setShowAdjustmentsPanel] = useState(false);

  const togglePrivacyMode = () => {
    const newValue = !privacyMode;
    setPrivacyMode(newValue);
    localStorage.setItem("performancePrivacyMode", newValue.toString());
  };

  const fetchShares = async () => {
    try {
      const response = await api.get('/shares');
      setShares(response.data);
    } catch (error) {
      console.log("Could not fetch shares (may be private):", error);
      setShares([]);
    }
  };

  const deleteShare = async (shareId) => {
    try {
      await api.delete(`/shares/${shareId}`);
      setShares(shares.filter(s => s.id !== shareId));
      toast.success("Share link deleted");
    } catch (error) {
      console.error("Error deleting share:", error);
      toast.error("Failed to delete share link");
    }
  };

  // Safe clipboard copy function with error handling
  const safeCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied to clipboard!");
      return true;
    } catch (error) {
      console.warn("Clipboard copy failed:", error);
      toast.error("Copy failed. Please copy the link manually.");
      return false;
    }
  };

  const copyShareLink = (shareId) => {
    const shareUrl = `${window.location.origin}/share/${shareId}`;
    safeCopyToClipboard(shareUrl);
  };

  // Balance Adjustment functions
  const fetchAdjustments = async () => {
    try {
      const response = await api.get('/balance-adjustments');
      setAdjustments(response.data);
    } catch (error) {
      console.log("Could not fetch adjustments (may be private):", error);
      setAdjustments([]);
    }
  };

  const openAdjustmentModal = () => {
    setAdjustmentAmount("");
    setAdjustmentType("deposit");
    setAdjustmentNote("");
    setAdjustmentDate("");
    setShowAdjustmentModal(true);
  };

  const closeAdjustmentModal = () => {
    setShowAdjustmentModal(false);
    setAdjustmentAmount("");
    setAdjustmentType("deposit");
    setAdjustmentNote("");
    setAdjustmentDate("");
  };

  const handleCreateAdjustment = async () => {
    if (!adjustmentAmount || isNaN(parseFloat(adjustmentAmount))) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSavingAdjustment(true);
    try {
      // For withdrawals and fees, make amount negative if positive
      let finalAmount = parseFloat(adjustmentAmount);
      if ((adjustmentType === "withdrawal" || adjustmentType === "fees") && finalAmount > 0) {
        finalAmount = -finalAmount;
      }
      // For deposits, make amount positive if negative
      if (adjustmentType === "deposit" && finalAmount < 0) {
        finalAmount = Math.abs(finalAmount);
      }

      const payload = {
        amount: finalAmount,
        adjustment_type: adjustmentType,
        note: adjustmentNote || null,
        date: adjustmentDate ? new Date(adjustmentDate).toISOString() : null
      };

      await api.post('/balance-adjustments', payload);
      toast.success("Adjustment added successfully");
      closeAdjustmentModal();
      fetchAdjustments();
      fetchMetrics(); // Refresh metrics to update capital
    } catch (error) {
      console.error("Error creating adjustment:", error);
      toast.error(error.response?.data?.detail || "Failed to add adjustment");
    } finally {
      setSavingAdjustment(false);
    }
  };

  const deleteAdjustment = async (adjustmentId) => {
    try {
      await api.delete(`/balance-adjustments/${adjustmentId}`);
      setAdjustments(adjustments.filter(a => a.id !== adjustmentId));
      fetchMetrics(); // Refresh metrics
      toast.success("Adjustment deleted");
    } catch (error) {
      console.error("Error deleting adjustment:", error);
      toast.error("Failed to delete adjustment");
    }
  };

  const getAdjustmentIcon = (type) => {
    switch (type) {
      case 'deposit': return <ArrowUp size={14} className="text-[#00E676]" />;
      case 'withdrawal': return <ArrowDown size={14} className="text-[#FF3B30]" />;
      case 'fees': return <Receipt size={14} className="text-[#FFC107]" />;
      case 'untracked_trades': return <CurrencyDollar size={14} className="text-white/60" />;
      default: return null;
    }
  };

  const getAdjustmentLabel = (type) => {
    switch (type) {
      case 'deposit': return 'Deposit';
      case 'withdrawal': return 'Withdrawal';
      case 'fees': return 'Fees';
      case 'untracked_trades': return 'Untracked Trades';
      default: return type;
    }
  };

  const totalAdjustments = adjustments.reduce((sum, adj) => sum + (adj.amount || 0), 0);

  useEffect(() => {
    fetchSettings();
    fetchMetrics();
    
    // Private endpoints - only fetch when NOT in shared/public profile mode
    if (!isSharedProfileView) {
      fetchShares();
      fetchAdjustments();
    }
  }, [isSharedProfileView]);

  useEffect(() => {
    if (dateFilter || startDate || endDate) {
      fetchMetrics();
    }
  }, [dateFilter, startDate, endDate]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setInitialCapital(response.data.initial_capital);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchMetrics = async () => {
    try {
      let params = {};
      
      if (dateFilter === "custom") {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      } else if (dateFilter) {
        const days = parseInt(dateFilter);
        const start = new Date();
        start.setDate(start.getDate() - days);
        params.start_date = start.toISOString();
      }

      const response = await api.get('/performance', { params });
      setMetrics(response.data);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      toast.error("Failed to load performance metrics");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCapital = async () => {
    try {
      const newCapital = parseFloat(tempCapital);
      if (isNaN(newCapital) || newCapital <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }

      await api.put(`/settings?initial_capital=${newCapital}`);
      setInitialCapital(newCapital);
      setEditingCapital(false);
      toast.success("Initial capital updated");
      fetchMetrics();
    } catch (error) {
      console.error("Error updating capital:", error);
      toast.error("Failed to update capital");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8" data-testid="performance-page">
      <div className="w-full max-w-[1600px] mx-auto">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight mb-2">
              {t('performance.title')}
            </h2>
            <p className="text-white/60 text-base font-sans">
              {language === 'ptBR' ? 'Acompanhe seu capital e performance de trading' : 'Track your capital and trading performance'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowShareModal(true)}
              variant="outline"
              data-testid="share-performance-button"
              className="border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/10 flex items-center gap-2"
            >
              <ShareNetwork size={20} weight="duotone" />
              <span className="hidden sm:inline">{language === 'ptBR' ? 'Compartilhar' : 'Share'}</span>
            </Button>
            <Button
              onClick={togglePrivacyMode}
              variant="outline"
              data-testid="privacy-toggle-button"
              className="border-white/10 text-white hover:bg-white/5 flex items-center gap-2"
              title={privacyMode ? (language === 'ptBR' ? 'Mostrar dados' : 'Show sensitive data') : (language === 'ptBR' ? 'Ocultar dados' : 'Hide sensitive data')}
            >
              {privacyMode ? <EyeSlash size={20} weight="duotone" /> : <Eye size={20} weight="duotone" />}
              <span className="hidden sm:inline">{privacyMode ? (language === 'ptBR' ? 'Mostrar' : 'Show') : (language === 'ptBR' ? 'Ocultar' : 'Hide')}</span>
            </Button>
          </div>
        </div>

        {/* Initial Capital & Adjustments */}
        <div className="mb-8 bg-[#121212] border border-white/10 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold font-heading mb-1">{language === 'ptBR' ? 'Capital da Conta' : 'Account Capital'}</h3>
              <p className="text-white/60 text-sm">{language === 'ptBR' ? 'Saldo inicial e ajustes' : 'Initial balance and adjustments'}</p>
            </div>
            <div className="flex gap-2">
              {/* Edit Capital and Add Adjustment - Hidden in shared/public profile mode */}
              {!isSharedProfileView && !editingCapital && (
                <Button
                  onClick={() => {
                    setEditingCapital(true);
                    setTempCapital(initialCapital.toString());
                  }}
                  data-testid="edit-capital-button"
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  {language === 'ptBR' ? 'Editar Capital' : 'Edit Capital'}
                </Button>
              )}
              {!isSharedProfileView && (
                <Button
                  onClick={openAdjustmentModal}
                  data-testid="add-adjustment-button"
                  className="bg-[#00E676]/10 text-[#00E676] hover:bg-[#00E676]/20 border border-[#00E676]/30"
                >
                  <Plus size={16} weight="bold" className="mr-1" />
                  Add Adjustment
                </Button>
              )}
            </div>
          </div>

          {/* Capital editing - Only shown in normal mode */}
          {!isSharedProfileView && editingCapital ? (
            <div className="flex gap-2 items-end mb-6">
              <div className="flex-1">
                <Label htmlFor="capital-input" className="text-white/80 mb-2 block">Initial Capital (USD)</Label>
                <Input
                  id="capital-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={tempCapital}
                  onChange={(e) => setTempCapital(e.target.value)}
                  data-testid="capital-input"
                  className="bg-[#0A0A0A] border-white/10 text-white font-mono text-lg"
                />
              </div>
              <Button
                onClick={handleUpdateCapital}
                data-testid="save-capital-button"
                className="bg-white text-black font-bold hover:bg-gray-200"
              >
                Save
              </Button>
              <Button
                onClick={() => setEditingCapital(false)}
                variant="outline"
                className="border-white/10 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                <div className="text-white/40 text-xs font-mono mb-1">
                  INITIAL CAPITAL
                  {isSharedProfileView && <Lock size={10} className="inline ml-1 text-white/30" />}
                </div>
                <div className="text-2xl font-bold font-mono text-white" data-testid="initial-capital-display">
                  {(privacyMode || isSharedProfileView) ? "*****" : `$${initialCapital.toLocaleString()}`}
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                <div className="text-white/40 text-xs font-mono mb-1">
                  ADJUSTMENTS
                  {isSharedProfileView && <Lock size={10} className="inline ml-1 text-white/30" />}
                </div>
                <div className={`text-2xl font-bold font-mono ${totalAdjustments >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                  {(privacyMode || isSharedProfileView) ? "*****" : `${totalAdjustments >= 0 ? '+' : ''}$${totalAdjustments.toLocaleString()}`}
                </div>
                <div className="text-white/40 text-xs mt-1">{adjustments.length} adjustment{adjustments.length !== 1 ? 's' : ''}</div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                <div className="text-white/40 text-xs font-mono mb-1">
                  EFFECTIVE CAPITAL
                  {isSharedProfileView && <Lock size={10} className="inline ml-1 text-white/30" />}
                </div>
                <div className="text-2xl font-bold font-mono text-white">
                  {(privacyMode || isSharedProfileView) ? "*****" : `$${(initialCapital + totalAdjustments).toLocaleString()}`}
                </div>
                <div className="text-white/40 text-xs mt-1">Before P&L</div>
              </div>
            </div>
          )}

          {/* Adjustments List */}
          {adjustments.length > 0 && (
            <div className="border-t border-white/10 pt-4">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowAdjustmentsPanel(!showAdjustmentsPanel)}
              >
                <span className="text-white/60 text-sm font-medium">
                  {showAdjustmentsPanel ? 'Hide' : 'Show'} Adjustment History ({adjustments.length})
                </span>
                <span className="text-white/40 text-xs">{showAdjustmentsPanel ? '▲' : '▼'}</span>
              </div>
              
              {showAdjustmentsPanel && (
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {adjustments.map((adj) => (
                    <div 
                      key={adj.id} 
                      className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-white/5 rounded-sm"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/5 rounded-full flex items-center justify-center">
                          {getAdjustmentIcon(adj.adjustment_type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium text-sm">
                              {getAdjustmentLabel(adj.adjustment_type)}
                            </span>
                            {adj.note && (
                              <span className="text-white/40 text-xs">• {adj.note}</span>
                            )}
                          </div>
                          <div className="text-white/40 text-xs">
                            {adj.date && format(new Date(adj.date), "MMM d, yyyy")}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`font-mono font-bold ${adj.amount >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                          {(privacyMode || isSharedProfileView) ? "*****" : `${adj.amount >= 0 ? '+' : ''}$${adj.amount.toLocaleString()}`}
                        </span>
                        {!isSharedProfileView && (
                          <Button
                            onClick={() => deleteAdjustment(adj.id)}
                            variant="ghost"
                            size="sm"
                            className="text-white/40 hover:text-[#FF3B30] hover:bg-transparent p-1"
                          >
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Time Filters */}
        <div className="mb-8 bg-[#121212] border border-white/10 rounded-sm p-6">
          <h3 className="text-lg font-bold font-heading mb-4">Time Period</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setDateFilter("7")}
              data-testid="filter-7days"
              className={`px-4 py-2 text-sm font-mono rounded-sm border transition-colors ${
                dateFilter === "7"
                  ? "bg-white text-black border-white"
                  : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
              }`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setDateFilter("30")}
              data-testid="filter-30days"
              className={`px-4 py-2 text-sm font-mono rounded-sm border transition-colors ${
                dateFilter === "30"
                  ? "bg-white text-black border-white"
                  : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
              }`}
            >
              Last 30 Days
            </button>
            <button
              onClick={() => setDateFilter("90")}
              data-testid="filter-90days"
              className={`px-4 py-2 text-sm font-mono rounded-sm border transition-colors ${
                dateFilter === "90"
                  ? "bg-white text-black border-white"
                  : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
              }`}
            >
              Last 90 Days
            </button>
            <button
              onClick={() => setDateFilter("custom")}
              data-testid="filter-custom"
              className={`px-4 py-2 text-sm font-mono rounded-sm border transition-colors ${
                dateFilter === "custom"
                  ? "bg-white text-black border-white"
                  : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
              }`}
            >
              Custom Range
            </button>
          </div>

          {dateFilter === "custom" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start-date" className="text-white/80 mb-2 block">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="start-date-input"
                  className="bg-[#0A0A0A] border-white/10 text-white"
                />
              </div>
              <div>
                <Label htmlFor="end-date" className="text-white/80 mb-2 block">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="end-date-input"
                  className="bg-[#0A0A0A] border-white/10 text-white"
                />
              </div>
            </div>
          )}
        </div>

        {/* Performance Metrics */}
        {metrics && (
          <div>
            <h3 className="text-2xl font-bold font-heading mb-4">Financial Performance</h3>
            <div className={`grid grid-cols-1 md:grid-cols-2 ${isSharedProfileView ? 'lg:grid-cols-2' : 'lg:grid-cols-4'} gap-4 mb-8`}>
              {/* Current Capital - HIDDEN in public/shared profile mode */}
              {!isSharedProfileView && (
                <MetricCard
                  title="Current Capital"
                  value={`$${metrics.current_capital.toLocaleString()}`}
                  subtitle="Including P&L"
                  icon={Wallet}
                  hidden={privacyMode}
                />
              )}
              {/* Total P&L - HIDDEN in public/shared profile mode */}
              {!isSharedProfileView && (
                <MetricCard
                  title="Total P&L"
                  value={`${metrics.total_profit_loss >= 0 ? '+' : ''}$${metrics.total_profit_loss.toLocaleString()}`}
                  subtitle={`From ${metrics.total_trades} trades`}
                  icon={ChartLineUp}
                  highlight={metrics.total_profit_loss >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}
                  hidden={privacyMode}
                />
              )}
              <MetricCard
                title="Return"
                value={`${metrics.return_percentage >= 0 ? '+' : ''}${metrics.return_percentage}%`}
                subtitle="Account growth"
                icon={Percent}
                highlight={metrics.return_percentage >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}
              />
              <MetricCard
                title="Win Rate"
                value={`${metrics.win_rate}%`}
                subtitle={`${metrics.winning_trades}W / ${metrics.losing_trades}L`}
                icon={TrendUp}
                highlight={metrics.win_rate >= 50 ? "text-[#00E676]" : "text-[#FFC107]"}
              />
            </div>

            {/* Trade Summary */}
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h3 className="text-lg font-bold font-heading mb-4">Trade Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">TOTAL TRADES</div>
                  <div className="text-2xl font-bold font-mono text-white">{metrics.total_trades}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">COMPLETED</div>
                  <div className="text-2xl font-bold font-mono text-white">{metrics.completed_trades}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">WINNING</div>
                  <div className="text-2xl font-bold font-mono text-[#00E676]">{metrics.winning_trades}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">LOSING</div>
                  <div className="text-2xl font-bold font-mono text-[#FF3B30]">{metrics.losing_trades}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Existing Share Links */}
        {shares.length > 0 && (
          <div className="mt-8 bg-[#121212] border border-white/10 rounded-sm p-6">
            <h3 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
              <LinkIcon size={20} weight="duotone" className="text-white/60" />
              Your Share Links
            </h3>
            <div className="space-y-3">
              {shares.map((share) => (
                <div 
                  key={share.id} 
                  className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-white/10 rounded-sm"
                  data-testid={`share-item-${share.id}`}
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="font-semibold text-white truncate">{share.title}</div>
                    <div className="text-white/40 text-xs font-mono truncate">
                      {window.location.origin}/share/{share.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => copyShareLink(share.id)}
                      variant="outline"
                      size="sm"
                      className="border-white/10 text-white hover:bg-white/5"
                      data-testid={`copy-share-${share.id}`}
                    >
                      <Copy size={16} weight="duotone" />
                    </Button>
                    <Button
                      onClick={() => window.open(`/share/${share.id}`, '_blank')}
                      variant="outline"
                      size="sm"
                      className="border-white/10 text-white hover:bg-white/5"
                      data-testid={`view-share-${share.id}`}
                    >
                      <Eye size={16} weight="duotone" />
                    </Button>
                    <Button
                      onClick={() => deleteShare(share.id)}
                      variant="outline"
                      size="sm"
                      className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                      data-testid={`delete-share-${share.id}`}
                    >
                      <Trash size={16} weight="duotone" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Share Modal */}
      <ShareModal 
        open={showShareModal} 
        onOpenChange={(open) => {
          setShowShareModal(open);
          if (!open) {
            // Refresh shares list when modal closes (in case a new share was created)
            fetchShares();
          }
        }}
        defaultTitle="My Trading Performance"
      />

      {/* Add Adjustment Modal */}
      <Dialog open={showAdjustmentModal} onOpenChange={setShowAdjustmentModal}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md" data-testid="adjustment-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Plus size={20} weight="bold" className="text-[#00E676]" />
              Add Balance Adjustment
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Record deposits, withdrawals, fees, or untracked trades to reconcile your account balance.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/80 mb-2 block">Type</Label>
              <Select value={adjustmentType} onValueChange={setAdjustmentType}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="deposit" className="text-white hover:bg-white/5">
                    <span className="flex items-center gap-2">
                      <ArrowUp size={14} className="text-[#00E676]" />
                      Deposit
                    </span>
                  </SelectItem>
                  <SelectItem value="withdrawal" className="text-white hover:bg-white/5">
                    <span className="flex items-center gap-2">
                      <ArrowDown size={14} className="text-[#FF3B30]" />
                      Withdrawal
                    </span>
                  </SelectItem>
                  <SelectItem value="fees" className="text-white hover:bg-white/5">
                    <span className="flex items-center gap-2">
                      <Receipt size={14} className="text-[#FFC107]" />
                      Fees
                    </span>
                  </SelectItem>
                  <SelectItem value="untracked_trades" className="text-white hover:bg-white/5">
                    <span className="flex items-center gap-2">
                      <CurrencyDollar size={14} className="text-white/60" />
                      Untracked Trades
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white/80 mb-2 block">
                Amount (USD) {adjustmentType === 'deposit' || adjustmentType === 'untracked_trades' ? '(positive)' : '(will be deducted)'}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                placeholder={adjustmentType === 'withdrawal' || adjustmentType === 'fees' ? "Enter amount to deduct" : "Enter amount"}
                className="bg-[#0A0A0A] border-white/10 text-white font-mono"
                data-testid="adjustment-amount-input"
              />
            </div>

            <div>
              <Label className="text-white/80 mb-2 block">Date (optional)</Label>
              <Input
                type="date"
                value={adjustmentDate}
                onChange={(e) => setAdjustmentDate(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
                data-testid="adjustment-date-input"
              />
            </div>

            <div>
              <Label className="text-white/80 mb-2 block">Note (optional)</Label>
              <Input
                type="text"
                value={adjustmentNote}
                onChange={(e) => setAdjustmentNote(e.target.value)}
                placeholder="e.g., Bank transfer, Trading fees"
                className="bg-[#0A0A0A] border-white/10 text-white"
                data-testid="adjustment-note-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              onClick={closeAdjustmentModal}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateAdjustment}
              disabled={savingAdjustment || !adjustmentAmount}
              className="bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90"
              data-testid="save-adjustment-button"
            >
              {savingAdjustment ? "Saving..." : "Add Adjustment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer Branding */}
      <div className="flex items-center justify-center gap-2 mt-16 mb-6 text-sm text-white/40">
        <img
          src={LOGO_PUREBRAIN}
          alt="GENI"
          className="h-5 w-auto object-contain opacity-80"
        />
        <span>
          Powered by <span className="text-white font-medium">GENI</span>
        </span>
      </div>
    </div>
  );
}
